/* eslint-disable @typescript-eslint/no-magic-numbers */
import type {
  CancellationPolicy,
  ItineraryIngredientResponse,
  HotelBookableAndLockableOption,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';

export type Comparison<T> = (item1: T, item2: T) => number;

export function on<TItem, TProperty>(
  selector: (item: TItem) => TProperty,
  comparer: Comparison<TProperty>
): Comparison<TItem> {
  return (x, y) => comparer(selector(x), selector(y));
}

export function onLocaleString<T>(
  projection: (item: T) => string
): Comparison<T> {
  return on(projection, localeStringComparer);
}

export const bookableOptionComparer = compare<HotelBookableAndLockableOption>(
  reversed((a, b) => booleanComparer(a.isLocked, b.isLocked)),
  reversed((a, b) => booleanComparer(a.isFromMeta, b.isFromMeta)),
  onNumber((a) => a.totalPrice.amount)
);

export const popularityAirportComparer = compare<{
  airport: { popularity: number } | null;
}>(reversed(onNumber((item) => item.airport?.popularity ?? 0)));

export function ingredientDateComparer(
  a: ItineraryIngredientResponse,
  b: ItineraryIngredientResponse,
  isTransport: (ingredient: ItineraryIngredientResponse) => boolean
): number {
  const semboDateA = new SemboDate(a.date);
  const semboDateB = new SemboDate(b.date);

  if (semboDateA.isSameDay(semboDateB.get())) {
    return isTransport(a) ? -1 : 1;
  }
  return semboDateA.isAfter(semboDateB.startOf('day').get()) ? 1 : -1;
}

export const stringComparer = compare(on(JSON.stringify, localeStringComparer));

export function cnxCompare(
  cnxA: CancellationPolicy,
  cnxB: CancellationPolicy
): number {
  return dateCompare(cnxA.fromDate, cnxB.fromDate);
}

export function propertyStringComparer<T>(
  propertySelector: (item: T) => string
): Comparison<T> {
  return compare<T>(onLocaleString(propertySelector));
}

export function dateCompare(
  dateA: Date | string,
  dateB: Date | string
): number {
  const semboDateA = new SemboDate(dateA);
  const semboDateB = new SemboDate(dateB);
  if (semboDateA.isSame(semboDateB.get())) {
    return 0;
  }
  return semboDateA.isAfter(semboDateB.get()) ? 1 : -1;
}

export function localeStringComparer(
  item1: string,
  item2: string,
  options?: Intl.CollatorOptions
): number {
  return item1.localeCompare(item2, undefined, options);
}

export function booleanComparer(item1: boolean, item2: boolean): number {
  if (item1 === item2) {
    return 0;
  } else {
    return item1 ? 1 : -1;
  }
}

export function compare<T>(
  ...comparisons: Array<Comparison<T>>
): Comparison<T> {
  let result: Comparison<T> = () => 0;

  for (const comparison of comparisons) {
    result = combineComparisons(result, comparison);
  }

  return result;
}

export function combineComparisons<T>(
  first: Comparison<T>,
  second: Comparison<T>
): Comparison<T> {
  return (x, y) => {
    const resultOfFirst = first(x, y);

    if (resultOfFirst !== 0) {
      return resultOfFirst;
    }

    return second(x, y);
  };
}

export function onNumber<T>(projection: (item: T) => number): Comparison<T> {
  return on(projection, numberComparer);
}

export function numberComparer(item1: number, item2: number): number {
  if (item1 === item2) {
    return 0;
  } else {
    return item1 < item2 ? -1 : 1;
  }
}

export function reversed<T>(comparison: Comparison<T>): Comparison<T> {
  return (x, y) => comparison(y, x);
}
